import { useQuery } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router-dom";
import { listOrgBooks, type Book } from "../api";
import BackLink from "../components/BackLink";
import { useT } from "../i18n";
import { primaryOrg, useSession } from "../session";
import NewTranslation from "../studio/NewTranslation";

/**
 * TranslatePage is `/studio/translate`: choosing a book, a destination, whether
 * the new book stays linked, and how it should read (requirement 0040).
 *
 * **It was a bottom sheet and outgrew it.** By requirement 0039 it held two mode
 * chips, the source books as tiles, sixteen language chips, the presets, two
 * kind chips, an instruction with its text area, the fixed rules, the estimate
 * and the primary action — inside `max-h-[70vh] overflow-y-auto`, with its own
 * sticky footer inside that. So the page scrolled, the sheet scrolled inside it,
 * and the action bar was drawn over the rules text, which is where the owner
 * found it.
 *
 * A bottom sheet is for a short, focused choice: one decision, little scrolling,
 * no nested scroll region and no competing primary action. A form with four
 * sections and a persistent action is a screen. Blank stays in the sheet, being
 * two fields.
 */
export default function TranslatePage() {
  const { me, isPending } = useSession();
  const org = primaryOrg(me);
  const navigate = useNavigate();
  const t = useT();

  // The same key the studio list uses, so arriving here from it costs nothing.
  const books = useQuery({
    queryKey: ["books", "org", org?.slug],
    queryFn: () => listOrgBooks(org!.slug),
    enabled: !!org,
  });

  if (!isPending && !org) return <Navigate to="/studio" replace />;

  const started = (book: Book) => navigate(`/studio/books/${book.id}`);

  return (
    <main
      data-testid="translate-page"
      /* The action bar is fixed to the screen, so the page reserves its height
         plus the home indicator. Nothing is ever drawn underneath it — which is
         the failure this screen exists to end. */
      className="mx-auto max-w-3xl px-4 pt-10 pb-[calc(7.5rem+env(safe-area-inset-bottom))]"
    >
      <nav className="mb-6 text-sm">
        <BackLink to="/studio">{t.book.backToStudio}</BackLink>
      </nav>
      <h1 className="mb-8 text-2xl font-semibold tracking-tight">{t.translation.translate}</h1>
      {books.data && <NewTranslation books={books.data.items} onStarted={started} />}
    </main>
  );
}
